import {UseQueryResult} from '@tanstack/react-query';
import {useMemo, useState} from 'react';

import {DataLoadingSwitch} from '../../../../../../../component/loading-switch/loading-switch';
import {Text} from '../../../../../../../component/text/text';
import {AdditionalInfo} from '../../../../../../../layout/additional-info/additional-info';
import {LegendBox} from '../../../../../../../layout/chart/legend-box/legend-box';
import {LegendWrapper} from '../../../../../../../layout/chart/legend-wrapper/legend-wrapper';
import {RadarChart} from '../../../../../../../layout/chart/radar-chart/radar-chart';
import {RadarDataSetType} from '../../../../../../../layout/chart/radar-chart/radar-chart-type';
import {PageCard} from '../../../../../../../layout/page-card/page-card';
import {useLocale} from '../../../../../../../provider/locale/locale-hook';
import {Locale} from '../../../../../../../provider/locale/localization';
import {ReviewsCountByAspectsType} from '../../../../../../../service/reivew-analysis/review-aspects/review-aspects-type';
import {useReviewsAnalysisFilter, useReviewsAnalysisTooltipFormatter} from '../../../../reviews-analysis-hook';
import {LegendBoxColorKeyEnum} from '../../../dashboard/dashboard-type';
import {getReviewCountByAspectsChartOptions} from '../aspects-helper';

import * as styles from './reviews-count-by-aspects.scss';

type PropsType = {
    reviewsCountByAspects: UseQueryResult<ReviewsCountByAspectsType>;
};

export function AspectsToneRadar({reviewsCountByAspects}: PropsType): JSX.Element {
    const {comparePeriodFilter} = useReviewsAnalysisFilter(false);
    const {data, isLoading, isError} = reviewsCountByAspects;

    const isEmpty = useMemo(() => {
        return data?.reviewsByAspects && data.reviewsByAspects.length === 0;
    }, [data]);

    const {getLocalizedString} = useLocale();

    const isCompareMode: boolean = useMemo(() => {
        return Boolean(comparePeriodFilter.startDate);
    }, [comparePeriodFilter.startDate]);

    const aspects = useMemo(() => {
        return (
            data?.reviewsByAspects.map((aspectData) => {
                return aspectData.aspect;
            }) || []
        );
    }, [data]);

    const [mainPeriodDatasetVisible, setMainPeriodDatasetVisible] = useState<boolean>(true);
    const [compareDatasetVisible, setCompareDatasetVisible] = useState<boolean>(true);

    const currentPeriodDataset: RadarDataSetType = useMemo(() => {
        return {
            label: getLocalizedString('REVIEWS_ANALYSIS__KEYWORDS_AND_TOPICS__ASPECTS__REVIEWS_BY_ASPECT__INDEX_TONE'),
            values:
                data?.reviewsByAspects.map((aspectData) => {
                    return aspectData.currentPeriodData?.tonalityIdx || 0;
                }) || [],
            color: {
                lineColor: styles.reviewsCountByAspectMainColor || '',
                areaColor: styles.reviewsCountByAspectMainAreaColor || '',
                legendBoxColorKey: LegendBoxColorKeyEnum.chart_1,
            },
        };
    }, [data, getLocalizedString]);

    const comparePeriodDataset: RadarDataSetType = useMemo(() => {
        return {
            label: 'compare',
            values:
                data?.reviewsByAspects.map((aspectData) => {
                    return aspectData.comparePeriodData?.tonalityIdx || 0;
                }) || [],
            color: {
                lineColor: styles.reviewsCountByAspectCompareColor || '',
                areaColor: styles.reviewsCountByAspectCompareAreaColor || '',
                legendBoxColorKey: LegendBoxColorKeyEnum.stripes,
            },
            dashed: true,
        };
    }, [data]);

    const getTooltipLabel = useReviewsAnalysisTooltipFormatter();

    return (
        <PageCard
            bodyClassName={styles.ReviewsCountByAspect}
            title={
                <AdditionalInfo
                    leftNode={
                        <Locale stringKey="REVIEWS_ANALYSIS__KEYWORDS_AND_TOPICS__ASPECTS__REVIEWS_BY_ASPECT__INDEX_TONE" />
                    }
                    popoverClassName={styles.ReviewsCountByAspect_additionalInfo}
                >
                    <Locale
                        stringKey="REVIEWS_ANALYSIS__KEYWORDS_AND_TOPICS__ASPECTS_TONE__ADDITIONAL_INFO"
                        valueMap={{
                            formula: (
                                <Text strong>
                                    <Locale stringKey="REVIEWS_ANALYSIS__KEYWORDS_AND_TOPICS__ASPECTS_TONE__ADDITIONAL_INFO_FORMULA" />
                                </Text>
                            ),
                        }}
                    />
                </AdditionalInfo>
            }
        >
            <DataLoadingSwitch isEmpty={isEmpty} isError={isError} isLoading={isLoading}>
                <LegendWrapper>
                    <LegendBox
                        checked={mainPeriodDatasetVisible}
                        color={styles.reviewsCountByAspectMainColor}
                        key="currentPeriod"
                        label={getLocalizedString(
                            'REVIEWS_ANALYSIS__KEYWORDS_AND_TOPICS__ASPECTS__REVIEWS_BY_ASPECT__INDEX_TONE'
                        )}
                        onChange={() => setMainPeriodDatasetVisible((previousState) => !previousState)}
                    />
                    {isCompareMode && (
                        <LegendBox
                            checked={compareDatasetVisible}
                            color={styles.reviewsCountByAspectCompareColor}
                            key="compare"
                            label={<Locale stringKey="REVIEWS_ANALYSIS__KEYWORDS_AND_TOPICS__COMPARE_PERIOD" />}
                            onChange={() => setCompareDatasetVisible((previousState) => !previousState)}
                        />
                    )}
                </LegendWrapper>
                <RadarChart
                    data={{
                        labels: aspects,
                        datasets: [
                            ...(mainPeriodDatasetVisible ? [currentPeriodDataset] : []),
                            ...(isCompareMode && compareDatasetVisible ? [comparePeriodDataset] : []),
                        ],
                    }}
                    options={getReviewCountByAspectsChartOptions({
                        aspects,
                        mainPeriodDataset: currentPeriodDataset,
                        tooltipLabelFormatter: getTooltipLabel,
                        comparePeriodDataset,
                        compareMode: isCompareMode,
                    })}
                />
            </DataLoadingSwitch>
        </PageCard>
    );
}
